import React from "react";

/** MUI */
import makeStyles from "@mui/styles/makeStyles";
import { Box, Grid, Typography } from "@mui/material";

/** Helpers */
import clsx from "clsx";
import { useTranslation } from "react-i18next";

/** UI */
import SronlyH2 from "@/src/components/srOnly/SronlyH2";

const useStyles = makeStyles((theme) => ({
    boxRoot: {
        // maxWidth: 696,
        // maxWidth: 'calc(100% - 266px)',
        maxWidth: "calc(100% - 10vw)",
        paddingTop: theme.spacing(0),
        paddingLeft: theme.spacing(2),
        "&:focus-visible": {
            ...theme.outliness,
        },
        "@media only screen and (min-width: 1921px)": {
            paddingLeft: theme.spacing(10),
        },
        [theme.breakpoints.down("sm")]: {
            display: "none",
        },
    },
    boxRootDialog: {
        paddingTop: theme.spacing(1),
        fontSize: "0.88888889rem",
        display: "block",
    },
    stepIconWraper: {
        maxWidth: 100,
        display: "flex",
        flexDirection: "column",
        flexWrap: "wrap",
        justifyContent: "center",
        alignItems: "center",
        minHeight: 210,
    },
    stepIconWraperDialog: {
        maxWidth: 50,
    },
    stepIcon: {
        width: 100,
        height: 100,
        borderRadius: "50%",
        border: 2,
        borderStyle: "solid",
        borderColor: theme.palette.primary.main,
        color: theme.palette.primary.main,
        fontSize: "2.4444444rem", //44,
        fontWeight: 600,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    stepIconDialog: {
        width: 50,
        height: 50,
        fontSize: "1.2222222rem",
    },
    dashedBorder: {
        borderStyle: "dashed",
    },
    greenBorder: {},
    stepContentWrapper: {
        padding: theme.spacing(1, 1, 1, 3),
        fontSize: "1.1111111rem",
        display: "flex",
        flexWrap: "wrap",
        flexDirection: "column",
        justifyContent: "center",
    },
    stepContentWrapperDialog: {
        fontSize: "0.875rem",
    },
    connector: {
        minHeight: 78, //48,
        width: 1,
        borderLeft: 2,
        borderLeftStyle: "solid",
        borderLeftColor: theme.palette.primary.main,
        margin: theme.spacing(1, 0),
    },
    semiConnectorTop: {
        minHeight: 39, //48,
        width: 1,
        borderLeft: 2,
        borderLeftStyle: "solid",
        borderLeftColor: theme.palette.primary.main,
        margin: theme.spacing(0, 0, 2, 0),
        flexGrow: 1,
    },
    semiConnectorBottom: {
        minHeight: 39, //48,
        width: 1,
        borderLeft: 2,
        borderLeftStyle: "solid",
        borderLeftColor: theme.palette.primary.main,
        margin: theme.spacing(2, 0, 0, 0),
        flexGrow: 1,
    },
    semiDashedConnectorTop: {
        minHeight: 39, //48,
        width: 1,
        borderLeft: 2,
        borderLeftStyle: "dashed",
        borderLeftColor: theme.palette.primary.main,
        margin: theme.spacing(0, 0, 2, 0),
        flexGrow: 1,
    },
    semiDashedConnectorBottom: {
        minHeight: 39, //48,
        width: 1,
        borderLeft: 2,
        borderLeftStyle: "dashed",
        borderLeftColor: theme.palette.primary.main,
        margin: theme.spacing(2, 0, 0.5, 0),
        flexGrow: 1,
    },
    semiConnectoEmpty: {
        minHeight: 39, //48,
        width: 1,
        borderLeft: 2,
        borderLeftStyle: "solid",
        borderLeftColor: "transparent", // theme.palette.primary.main,
        margin: theme.spacing(1, 0, 1, 0),
        flexGrow: 1,
    },
    dashedConnector: {
        borderLeftStyle: "dashed",
    },
    connectorDialog: {
        // height: "calc(100% - 66px)",
        // display:'none',
        flexGrow: 1,
    },
    greenBorderLeft: {},
    srOnly: {
        position: "absolute",
        width: 1,
        height: 1,
        padding: 0,
        margin: -1,
        overflow: "hidden",
        clip: "rect(0, 0, 0, 0)",
        border: 0,
    },
}));

const StepIcon = (props) => {
    const { dashed, isInDialog, selectedIndicators, ...rest } = props;
    const classes = useStyles();
    return (
        <Box
            className={clsx(
                classes.stepIcon,
                isInDialog && classes.stepIconDialog,
                dashed && classes.dashedBorder,
                selectedIndicators?.length && classes.greenBorder
            )}
            {...rest}
        >
            {props.children}
        </Box>
    );
};

const Steps = (props) => {
    const { isInDialog, selectedIndicators } = props;
    const { t } = useTranslation();
    const classes = useStyles();

    return (
        <Box className={clsx(classes.boxRoot, isInDialog && classes.boxRootDialog)} id={"db-howto-steps"} tabIndex={-1}>
            <SronlyH2>{t("Cztery kroki - krótki opis jak wygenerować i zmodyfikować zestawienie")}</SronlyH2>
            <Grid container spacing={0}>
                <Grid item xs className={clsx(classes.stepIconWraper, isInDialog && classes.stepIconWraperDialog)}>
                    <Box
                        className={clsx(
                            classes.semiConnectoEmpty,
                            isInDialog && classes.connectorDialog,
                            selectedIndicators?.length && classes.greenBorderLeft
                        )}
                    />
                    <StepIcon dashed={false} selectedIndicators={selectedIndicators} isInDialog={isInDialog}>
                        <span className={classes.srOnly}>{t(`Krok `)}</span>1
                    </StepIcon>
                    <Box
                        className={clsx(
                            classes.semiConnectorBottom,
                            isInDialog && classes.connectorDialog,
                            selectedIndicators?.length && classes.greenBorderLeft
                        )}
                    />
                </Grid>
                <Grid
                    item
                    xs
                    className={clsx(classes.stepContentWrapper, isInDialog && classes.stepContentWrapperDialog)}
                >
                    <Typography variant={"body1"} sx={{ color: "primary.main", mb: 1, fontSize: "inherit" }}>
                        <b>{t("Wybór danych")}</b>
                    </Typography>
                    <Typography variant={"body2"} sx={{ fontSize: "inherit" }}>
                        {t(
                            "Zaznacz 1 lub kilka dowolnych zmiennych, w drzewku po lewej stronie (możesz też skorzystać z wyszukiwarki nad drzewkiem)"
                        )}
                        {". "}
                        {t("Kliknij")}{" "}
                        <Typography
                            variant={"body2"}
                            sx={{ color: "primary.main", fontWeight: 600, fontSize: "inherit" }}
                            component={"span"}
                        >
                            {t("Wygeneruj zestawienie")}
                        </Typography>
                        {"."}
                    </Typography>
                </Grid>
            </Grid>

            <Grid container spacing={0}>
                <Grid item xs className={clsx(classes.stepIconWraper, isInDialog && classes.stepIconWraperDialog)}>
                    <Box className={clsx(classes.semiConnectorTop, isInDialog && classes.connectorDialog)} />
                    <StepIcon dashed={true} isInDialog={isInDialog}>
                        <span className={classes.srOnly}>{t(`Krok `)}</span>2
                    </StepIcon>
                    <Box className={clsx(classes.semiDashedConnectorBottom, isInDialog && classes.connectorDialog)} />
                </Grid>
                <Grid
                    item
                    xs
                    className={clsx(classes.stepContentWrapper, isInDialog && classes.stepContentWrapperDialog)}
                >
                    <Typography variant={"body1"} sx={{ color: "primary.main", mb: 1, fontSize: "inherit" }}>
                        <b>{t("Szereg czasowy i jednostka terytorialna")}</b>
                    </Typography>
                    <Typography variant={"body2"} sx={{ fontSize: "inherit" }}>
                        {t("Jeśli chcesz, zmień")} <b>{t("okres lub poziom terytorialny")}</b>
                        {". "}
                        {t("Kliknij")}{" "}
                        <Typography
                            variant={"body2"}
                            sx={{ color: "primary.main", fontWeight: 600, fontSize: "inherit" }}
                            component={"span"}
                        >
                            {t("Zatwierdź")}
                        </Typography>
                        {"."}
                    </Typography>
                </Grid>
            </Grid>

            <Grid container spacing={0}>
                <Grid item xs className={clsx(classes.stepIconWraper, isInDialog && classes.stepIconWraperDialog)}>
                    <Box className={clsx(classes.semiDashedConnectorTop, isInDialog && classes.connectorDialog)} />
                    <StepIcon dashed={true} isInDialog={isInDialog}>
                        <span className={classes.srOnly}>{t(`Krok `)}</span>3
                    </StepIcon>
                    <Box className={clsx(classes.semiConnectorBottom, isInDialog && classes.connectorDialog)} />
                </Grid>
                <Grid
                    item
                    xs
                    className={clsx(classes.stepContentWrapper, isInDialog && classes.stepContentWrapperDialog)}
                >
                    <Typography variant={"body1"} sx={{ color: "primary.main", mb: 1, fontSize: "inherit" }}>
                        <b>{t("Filtry")}</b>
                    </Typography>
                    <Typography variant={"body2"} sx={{ fontSize: "inherit" }}>
                        {t("Jeśli chcesz, w polu Wybór zaawansowany zmień")}{" "}
                        <b>{t("przekrój i pozycje wybranych wymiarów")}</b>
                        {". "}
                        {t("Możesz wybrać też dowolny")} <b>{t("typ informacji")}</b>
                        {". "}
                        {t("Kliknij")}{" "}
                        <Typography
                            variant={"body2"}
                            sx={{ color: "primary.main", fontWeight: 600, fontSize: "inherit" }}
                            component={"span"}
                        >
                            {t("Zatwierdź")}
                        </Typography>
                        {"."}
                    </Typography>
                </Grid>
            </Grid>

            <Grid container spacing={0}>
                <Grid item xs className={clsx(classes.stepIconWraper, isInDialog && classes.stepIconWraperDialog)}>
                    <Box className={clsx(classes.semiConnectorTop, isInDialog && classes.connectorDialog)} />
                    <StepIcon dashed={false} isInDialog={isInDialog}>
                        <span className={classes.srOnly}>{t(`Krok `)}</span>4
                    </StepIcon>
                    <Box
                        className={clsx(
                            classes.semiConnectoEmpty,
                            isInDialog && classes.connectorDialog,
                            selectedIndicators?.length && classes.greenBorderLeft
                        )}
                    />
                </Grid>
                <Grid
                    item
                    xs
                    className={clsx(classes.stepContentWrapper, isInDialog && classes.stepContentWrapperDialog)}
                >
                    <Typography variant={"body1"} sx={{ color: "primary.main", mb: 1, fontSize: "inherit" }}>
                        <b>{t("Gotowa tablica")}</b>
                    </Typography>
                    <Typography variant={"body2"} sx={{ fontSize: "inherit" }}>
                        {t("Tablica znajduje się poniżej pola Wybór zaawansowany")}
                        {". "}
                    </Typography>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Steps;
